import { Check } from 'lucide-react';
import { useEffect } from 'react';

const StreakTracker = ({ streakData = [], currentStreak = 0 }) => {
    const allDaysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    // Generate day labels for the last 7 days (Monday-first week)
    const getDaysOfWeekLabels = () => {
        const today = new Date();
        const todayDayIndex = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
        // Convert to Monday-first week index: Monday = 0, Sunday = 6
        const todayWeekIndex = (todayDayIndex - 1 + 7) % 7;
        const labels = [];

        // Start from 6 days ago
        for (let i = 6; i >= 0; i--) {
            const dayIndex = (todayWeekIndex - i + 7) % 7;
            labels.push(allDaysOfWeek[dayIndex]);
        }
        return labels;
    };

    const daysOfWeek = getDaysOfWeekLabels();

    // Inject CSS for a visible scrollbar
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const styleId = 'xp-calendar-scrollbar-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
        .xp-calendar-scrollbar::-webkit-scrollbar { height: 8px; }
        .xp-calendar-scrollbar::-webkit-scrollbar { height: 8px !important; display: block !important; }
        .xp-calendar-scrollbar::-webkit-scrollbar-track { background: hsl(var(--muted)) !important; border-radius: 10px; }
        .xp-calendar-scrollbar::-webkit-scrollbar-thumb { background: hsl(var(--border)) !important; border-radius: 10px; }
        .xp-calendar-scrollbar::-webkit-scrollbar-thumb:hover { background: hsl(var(--muted-foreground)) !important; }
        /* For Firefox */
        .xp-calendar-scrollbar { scrollbar-width: thin !important; scrollbar-color: hsl(var(--border)) hsl(var(--muted)) !important; }

        .dark .xp-calendar-scrollbar::-webkit-scrollbar-track { background: hsl(var(--muted)); }
        .dark .xp-calendar-scrollbar::-webkit-scrollbar-thumb { background: hsl(var(--secondary)); }
        .dark .xp-calendar-scrollbar::-webkit-scrollbar-thumb:hover { background: hsl(var(--secondary-foreground)); }
        /* For Firefox - Dark Mode */
        .dark .xp-calendar-scrollbar { scrollbar-color: hsl(var(--secondary)) hsl(var(--muted)) !important; }
      `;
            document.head.appendChild(style);
        }
    }, []);

    // Calculate hours remaining to recover streak
    const getHoursRemaining = () => {
        const now = new Date();
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        const msRemaining = endOfDay - now;
        return Math.ceil(msRemaining / (1000 * 60 * 60));
    };

    // Check if streak is at risk (yesterday completed, today not)
    const isStreakAtRisk = () => {
        if (streakData.length < 2) return false;
        const yesterday = streakData[streakData.length - 2];
        const today = streakData[streakData.length - 1];
        return yesterday?.completed && !today?.completed;
    };

    const hoursRemaining = getHoursRemaining();
    const streakAtRisk = isStreakAtRisk();

    return (
        <div className="w-full py-2 rounded-lg">
            <div className="pt-4 px-4">
                <h5 className="mb-1 capitalize">{currentStreak} Day Streak</h5>
                <p className="tet-sm">Earn XP by taking a learning action today</p>
                {streakAtRisk && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800">
                            ⚠️ You have{' '}
                            <span className="font-semibold">{hoursRemaining} hours</span> to recover
                            today&apos;s streak
                        </p>
                    </div>
                )}
            </div>

            <div className="overflow-x-auto xp-calendar-scrollbar p-2">
                <div className="flex justify-between items-center gap-1 px-1">
                    {daysOfWeek.map((day, index) => {
                        const dayData = streakData[index] || { completed: false };
                        const isCompleted = dayData.completed;
                        const isToday = index === daysOfWeek.length - 1;

                        return (
                            <div
                                key={index}
                                className="flex flex-col items-center justify-center gap-1"
                            >
                                <span className="text-sm font-medium text-neutral-700">{day}</span>
                                <div
                                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                        isCompleted
                                            ? 'bg-success'
                                            : isToday
                                              ? 'border-2 border-dashed border-neutral-300'
                                              : 'bg-neutral-300'
                                    }`}
                                >
                                    {isCompleted && (
                                        <Check className="w-4 h-4 text-white stroke-3" />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default StreakTracker;
